import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useLogout, useMe } from "../api/hooks";
import { useT } from "../i18n";
import { Button } from "../ui/Button";
import { HandbookLink } from "../ui/HandbookLink";
import { Settings } from "lucide-react";
import { LanguageSwitcher } from "../ui/LanguageSwitcher";
import { KetcherPanel } from "../chem/KetcherPanel";
import { WorkspaceGuard } from "./WorkspaceGuard";

export function AppShell() {
  const me = useMe();
  const t = useT();
  const logout = useLogout();
  const navigate = useNavigate();
  return (
    <div className="flex h-screen flex-col">
      <header className="flex items-center gap-4 border-b border-neutral-200 bg-[var(--ground)] px-7 py-3.5 text-sm dark:border-neutral-800">
        <Link to="/" className="flex items-center gap-2.5 text-[15px] font-semibold tracking-[-0.01em] text-neutral-900 dark:text-neutral-100">
          <span className="grid h-7 w-7 place-items-center rounded-[7px] bg-accent text-[13px] font-bold tracking-[0.02em] text-white">K</span>
          {/* the full name, and its short form where a phone has no room for it (spec 0.55) */}
          <span className="hidden whitespace-nowrap sm:inline">Knowledge Storage System</span>
          <span className="sm:hidden" title="Knowledge Storage System">
            KSS
          </span>
        </Link>
        <div className="grow" />
        {me.data?.isAdmin && (
          <Link
            to="/settings"
            className="flex items-center gap-1.5 text-[13.5px] text-neutral-500 transition hover:text-neutral-900 dark:hover:text-neutral-100"
            data-testid="server-settings-link"
          >
            <Settings size={15} aria-hidden="true" />
            {t("server.link")}
          </Link>
        )}
        <HandbookLink />
        <LanguageSwitcher />
        {me.data && (
          // [M28] the account page: the MCP connector and its tokens (spec §8.27)
          <Link
            to="/account"
            className="flex items-center gap-2 text-[13.5px] text-neutral-500 transition hover:text-neutral-900 dark:hover:text-neutral-100"
            title={t("app.account")}
            data-testid="account-link"
          >
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: me.data.color }} />
            {me.data.display_name || me.data.username}
          </Link>
        )}
        <Button
          variant="ghost"
          onClick={() =>
            logout.mutateAsync().then((endSessionUrl) => {
              // an OIDC session also ends at Keycloak, which then returns to /login (spec §19.1)
              if (endSessionUrl) window.location.assign(endSessionUrl);
              else void navigate({ to: "/login", search: { next: undefined } });
            })
          }
        >
          {t("app.logOut")}
        </Button>
      </header>
      <main className="min-h-0 grow overflow-auto">
        <WorkspaceGuard>
          <Outlet />
        </WorkspaceGuard>
      </main>
      <KetcherPanel />
    </div>
  );
}
